import React, { useState } from 'react'
import { useAuthContext } from '../../context/auth.context'
import { useApiFormAuthLogout } from '../../api-new/state/auth'
import { removeTokens } from '../../storage/auth'
import '../../shared/sso-workspace/sso-workspace.scss'
import './impersonation-banner.scss'

// Who an impersonated tab is viewing as: the name on the token, else its
// email.
export const impersonatedName = claims => {
  const name = `${(claims && claims.firstName) || ''} ${(claims && claims.lastName) || ''}`.trim()
  return name || (claims && claims.email) || 'this person'
}

// "End impersonation": the server ends the whole impersonation -- this
// session and any device or launch session started from it -- and records
// it (DELETE /auth-logout/:refreshToken?scope=impersonation). Only then is
// this tab's copy dropped; a failure keeps the session so the admin can
// retry rather than leave it running unseen.
export const endImpersonation = async ({ refreshToken, logout, clearTokens, closeTab }) => {
  // POST /auth-logout with the token in the body, never the URL.
  await logout({ refreshToken, return: 'admin', scope: 'impersonation' })
  clearTokens()
  closeTab()
}

const endsAtLabel = seconds => {
  try {
    return new Date(seconds * 1000).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  } catch (e) {
    return null
  }
}

// The persistent strip an impersonated tab shows above everything else
// (owner ruling 2026-10-01). The token says it is an impersonation:
// impersonationEndsAt, which auth mints only for an impersonated session
// (display only -- every security decision is made server-side).
export const ImpersonationBanner = ({ claims, onEnd }) => {
  const [phase, setPhase] = useState('open') // open | ending | ended | failed

  if (!claims || !claims.impersonationEndsAt) return null

  if (phase === 'ended') {
    return (
      <div className="impersonation-banner" role="status">
        <span className="impersonation-banner__text">Impersonation ended. You can close this tab.</span>
      </div>
    )
  }

  const handleEnd = async () => {
    if (phase === 'ending') return
    setPhase('ending')
    try {
      await onEnd()
      setPhase('ended')
    } catch (e) {
      setPhase('failed')
    }
  }

  const until = endsAtLabel(claims.impersonationEndsAt)

  return (
    <div className="impersonation-banner" role="status" data-testid="impersonation-banner">
      <span className="impersonation-banner__pill">Impersonating</span>
      <span className="impersonation-banner__text">
        Viewing as <strong className="impersonation-banner__name">{impersonatedName(claims)}</strong>
        {until && <span className="impersonation-banner__until"> · ends {until}</span>}
      </span>
      {phase === 'failed' &&
        <span className="impersonation-banner__error" role="alert">The impersonation couldn't end. Try again.</span>}
      <button type="button" className="btn btn-sm sw-btn impersonation-banner__end" disabled={phase === 'ending'} onClick={handleEnd}>
        {phase === 'ending' ? 'Ending…' : 'End impersonation'}
      </button>
    </div>
  )
}

// After ending: close the tab (it was opened for this impersonation), or,
// when the browser won't let a page close itself, show the ended page.
const closeTab = () => {
  window.close()
  window.location.replace('/open/impersonation-ended')
}

export const ImpersonationBannerConnected = () => {
  const authContext = useAuthContext()
  const apiFormAuthLogout = useApiFormAuthLogout()

  return (
    <ImpersonationBanner
      claims={authContext.state.jwtClaims}
      onEnd={() => endImpersonation({
        refreshToken: authContext.state.refreshToken,
        logout: body => apiFormAuthLogout.post(body),
        clearTokens: () => removeTokens(authContext.state.isSession),
        closeTab
      })} />
  )
}
